import React, { useState } from 'react';

const MetaBudgetReviewQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [actionType, setActionType] = useState('comment_added');
  const [newStatus, setNewStatus] = useState(record?.estado || 'pendiente');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Estados para agregar presupuesto
  const [newBudgetAmount, setNewBudgetAmount] = useState('');
  const [newPaymentDate, setNewPaymentDate] = useState(new Date().toISOString().split('T')[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!comment.trim() && actionType === 'comment_added') {
      alert('Por favor ingrese un comentario');
      return;
    }

    setLoading(true);
    try {
      const actionData = {
        action_type: actionType,
        comment: comment.trim(),
        user_name: currentUser?.name || currentUser?.email || 'Sistema'
      };

      if (actionType === 'status_changed') {
        actionData.old_status = record.estado;
        actionData.new_status = newStatus;
      }

      await onSave(record.id, actionData);
    } catch (error) {
      console.error('Error al guardar acción:', error);
      alert(`Error al registrar la acción: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = (status) => {
    const texts = {
      'pendiente': 'Pendiente',
      'en_proceso': 'En Proceso',
      'completado': 'Completado',
      'cancelado': 'Cancelado'
    };
    return texts[status] || status;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX');
  };

  const calculateDaysRemaining = (fechaVencimiento) => {
    if (!fechaVencimiento) return 0;
    const today = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diffTime = vencimiento - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const porcentajeRestante = parseFloat(record?.porcentaje_restante) || 0;
  const diasRestantes = calculateDaysRemaining(record?.fecha_vencimiento);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">⚡ Acción Rápida - Revisión de Presupuesto</h2>
              <p className="text-blue-100 mt-1">
                Registrar acción para: {record?.cliente}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Información del registro */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">📊 Información del Presupuesto</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="space-y-2">
                  <div><strong>Cliente:</strong> {record?.cliente}</div>
                  <div><strong>Campaña:</strong> {record?.nombre_campana || 'N/A'}</div>
                  <div><strong>Agente:</strong> {record?.agente_seguimiento || 'N/A'}</div>
                  <div><strong>Célula:</strong> {record?.celula || 'N/A'}</div>
                </div>
              </div>
              <div>
                <div className="space-y-2">
                  <div><strong>Monto Total:</strong> {formatCurrency(record?.monto_pago)}</div>
                  <div><strong>Presupuesto Diario:</strong> {formatCurrency(record?.presupuesto_diario)}</div>
                  <div><strong>Saldo Disponible:</strong> {formatCurrency(record?.saldo_disponible)}</div>
                  <div><strong>Días Restantes:</strong> {diasRestantes} días</div>
                </div>
              </div>
            </div>
            
            {/* Barra de progreso del presupuesto */}
            <div className="mt-4">
              <div className="flex justify-between text-xs mb-1">
                <span>Porcentaje restante:</span>
                <span className="font-medium">{porcentajeRestante.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className={`h-3 rounded-full transition-all duration-300 ${
                    porcentajeRestante >= 70 ? 'bg-green-500' :
                    porcentajeRestante >= 40 ? 'bg-yellow-500' :
                    porcentajeRestante >= 20 ? 'bg-orange-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.max(0, Math.min(100, porcentajeRestante))}%` }}
                ></div>
              </div>
              <div className="text-xs text-center mt-1 font-medium">
                {porcentajeRestante >= 70 ? 'Presupuesto saludable' :
                 porcentajeRestante >= 40 ? 'Presupuesto en seguimiento' :
                 porcentajeRestante >= 20 ? 'Presupuesto bajo - Atención requerida' : 
                 'Presupuesto crítico - Acción inmediata'}
              </div>
            </div>
          </div>

          {/* Formulario de acción */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Acción
              </label>
              <select
                value={actionType}
                onChange={(e) => setActionType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="comment_added">💬 Agregar Comentario</option>
                <option value="status_changed">🔄 Cambiar Estado</option>
                <option value="budget_added">💰 Agregar Presupuesto</option>
                <option value="completed">✅ Marcar como Completado</option>
                <option value="cancelled">❌ Cancelar</option>
              </select>
            </div>

            {/* Alerta de presupuesto bajo */}
            {porcentajeRestante < 30 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">
                      ⚠️ Presupuesto Crítico
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <p>
                        El presupuesto está en nivel crítico ({porcentajeRestante.toFixed(1)}%). 
                        Considera agregar más presupuesto para continuar con la campaña.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {actionType === 'status_changed' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nuevo Estado
                </label>
                <div className="flex items-center space-x-4 mb-2">
                  <span className="text-sm text-gray-600">
                    Estado actual: <span className="font-medium">{getStatusText(record?.estado)}</span>
                  </span>
                </div>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pendiente">Pendiente</option>
                  <option value="en_proceso">En Proceso</option>
                  <option value="completado">Completado</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
            )}

            {/* Campos para agregar presupuesto */}
            {actionType === 'budget_added' && (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-green-800">
                        💰 Agregar Nuevo Presupuesto
                      </h3>
                      <div className="mt-2 text-sm text-green-700">
                        <p>
                          Esto agregará presupuesto adicional al registro existente y recalculará automáticamente 
                          los valores de fecha de vencimiento, presupuesto diario y saldo disponible.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nuevo Monto de Pago *
                    </label>
                    <input
                      type="number"
                      value={newBudgetAmount}
                      onChange={(e) => setNewBudgetAmount(e.target.value)}
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Monto adicional que se agregará al presupuesto
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nueva Fecha de Pago *
                    </label>
                    <input
                      type="date"
                      value={newPaymentDate}
                      onChange={(e) => setNewPaymentDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Fecha del nuevo pago (se calculará nueva fecha de vencimiento)
                    </p>
                  </div>
                </div>

                {/* Preview de los nuevos cálculos */}
                {newBudgetAmount && parseFloat(newBudgetAmount) > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">📊 Preview de Nuevos Cálculos</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="text-center">
                        <span className="block text-blue-700 font-medium">Nuevo Monto Total</span>
                        <span className="text-green-600 font-semibold">
                          {formatCurrency((parseFloat(record?.monto_pago) || 0) + parseFloat(newBudgetAmount))}
                        </span>
                      </div>
                      <div className="text-center">
                        <span className="block text-blue-700 font-medium">Nueva Fecha Vencimiento</span>
                        <span className="text-gray-900">
                          {(() => {
                            const newDate = new Date(newPaymentDate);
                            newDate.setDate(newDate.getDate() + 30);
                            return newDate.toLocaleDateString('es-MX');
                          })()}
                        </span>
                      </div>
                      <div className="text-center">
                        <span className="block text-blue-700 font-medium">Nuevo Presupuesto Diario</span>
                        <span className="text-green-600 font-semibold">
                          {formatCurrency(parseFloat(newBudgetAmount) / 30)}
                        </span>
                      </div>
                      <div className="text-center">
                        <span className="block text-blue-700 font-medium">Saldo Total Disponible</span>
                        <span className="text-blue-600 font-semibold">
                          {formatCurrency((parseFloat(record?.saldo_disponible) || 0) + parseFloat(newBudgetAmount))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {actionType === 'comment_added' ? 'Comentario *' : 'Comentario (opcional)'}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={
                  actionType === 'status_changed' 
                    ? 'Razón del cambio de estado...'
                    : actionType === 'completed'
                    ? 'Detalles de la finalización...'
                    : actionType === 'cancelled'
                    ? 'Motivo de la cancelación...'
                    : 'Escriba su comentario aquí...'
                }
              />
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={loading}
              >
                {loading ? 'Guardando...' : 'Registrar Acción'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MetaBudgetReviewQuickActionModal;
